import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Container from "@/components/editorial/Container";
import Eyebrow from "@/components/editorial/Eyebrow";
import SponsorStrip from "@/components/editorial/SponsorStrip";
import { fetchEvents } from "@/lib/events";

const SITE_URL = "https://mujeres-motor.com";
const EVENTS_HERO = "https://static.prod-images.emergentagent.com/jobs/fcfb0e3d-78af-4eb0-9bc1-020bea71b151/images/bc7c230eb557ff7b17ccd13037ad301730456f1f77aaa55c71b84bf88ac060c1.jpeg";

const TYPE_LABELS = { screening: "Screening", panel: "Panel", networking: "Networking", summit: "Summit", workshop: "Workshop" };

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

function EventCard({ ev, featured = false }) {
  return (
    <Link to={`/events/${ev.slug}`} data-testid={`event-card-${ev.slug}`} className={`group block ${featured ? "lg:col-span-2" : ""}`}>
      <div className={`relative overflow-hidden rounded-sm bg-charcoal-soft mb-5 ${featured ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
        {ev.cover_image_url && (
          <img src={ev.cover_image_url} alt={ev.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />
        <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-cream text-charcoal px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em]">
          {TYPE_LABELS[ev.event_type] || ev.event_type}
        </div>
      </div>
      <p className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-2">{ev.city} · {formatDate(ev.start_at)}</p>
      <h3 className={`font-serif font-light text-charcoal group-hover:text-terracotta transition-colors mb-2 leading-tight ${featured ? "text-3xl md:text-4xl" : "text-2xl"}`}>{ev.title}</h3>
      <p className="text-sm text-charcoal/70 line-clamp-2">{ev.summary}</p>
      <span className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-terracotta">RSVP <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" /></span>
    </Link>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents().then((list) => { setEvents(list); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const upcoming = events.filter((e) => e.status === "upcoming").sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  const past = events.filter((e) => e.status === "past");
  const featured = upcoming[0];
  const rest = upcoming.slice(1);

  return (
    <SiteLayout transparent>
      <></>

      {/* Hero */}
      <section data-testid="events-hero" className="relative min-h-[60vh] flex items-end bg-charcoal text-cream overflow-hidden pb-14">
        <img src={EVENTS_HERO} alt="Eventos Mujeres Motor" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/30 via-charcoal/50 to-charcoal" />
        <Container className="relative z-10">
          <Eyebrow tone="gold" className="mb-5 block">Eventos</Eyebrow>
          <h1 className="font-serif font-light text-[clamp(2.25rem,5vw,4.5rem)] leading-[1.03] max-w-4xl">
            Experiencias en vivo con <span className="italic">la comunidad.</span>
          </h1>
          <p className="mt-6 text-cream/75 text-lg max-w-2xl">
            Screenings, paneles, cenas y summits privados en las ciudades donde la red se está armando.
          </p>
        </Container>
      </section>

      {/* Listing */}
      <section className="bg-cream py-20 md:py-28" data-testid="events-listing">
        <Container>
          {loading ? (
            <p className="text-charcoal/50">Cargando eventos…</p>
          ) : featured ? (
            <>
              <Eyebrow tone="terracotta" className="mb-6 block">Próximo evento</Eyebrow>
              <div className="mb-16">
                <EventCard ev={featured} featured />
              </div>
              {rest.length > 0 && (
                <>
                  <Eyebrow tone="terracotta" className="mb-6 block">Agenda</Eyebrow>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    {rest.map((ev) => <EventCard key={ev.id} ev={ev} />)}
                  </div>
                </>
              )}
            </>
          ) : (
            <p className="text-center font-serif italic text-xl text-charcoal/60 py-16">Próximamente anunciamos nuevos eventos.</p>
          )}

          {past.length > 0 && (
            <details className="mt-20 group" data-testid="events-past">
              <summary className="cursor-pointer text-sm uppercase tracking-[0.22em] text-charcoal/60 hover:text-terracotta">
                Eventos pasados <span className="text-terracotta">({past.length})</span>
              </summary>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {past.map((ev) => <EventCard key={ev.id} ev={ev} />)}
              </div>
            </details>
          )}
        </Container>
      </section>

      {/* Founding mention */}
      <section className="bg-cream-dark py-20 border-t border-charcoal/5">
        <Container>
          <SponsorStrip tier="founding" eyebrow="Eventos presentados por" tone="light" size="md" centered testid="events-founding" />
          <div className="mt-10">
            <SponsorStrip tier="content" eyebrow="Co-presenta" tone="light" size="sm" centered testid="events-content" />
          </div>
        </Container>
      </section>
    </SiteLayout>
  );
}
